import React from "react";
import { FormGroup } from "reactstrap";
import ReactTooltip from "react-tooltip";
import withStyles from "isomorphic-style-loader/withStyles";
import BaseInput from "../../../toolkit/baseInput";
import s from "./thresholdFields.scss";
import { parseWholeDollar } from "./helpers";

export interface ThresholdState {
  threshold: string;
  maxThreshold: string;
  firstMonthlyThreshold: string;
  maxFirstMonthlyThreshold: string;
}

export const BLANK_THRESHOLDS: ThresholdState = {
  threshold: "",
  maxThreshold: "",
  firstMonthlyThreshold: "",
  maxFirstMonthlyThreshold: "",
};

export interface ParsedThresholds {
  threshold: number | null;
  maxThreshold: number | null;
  firstMonthlyThreshold: number | null;
  maxFirstMonthlyThreshold: number | null;
}

export function readThresholds(
  fields: ThresholdState
): ParsedThresholds | null {
  const parseOptional = (raw: string): number | null | undefined => {
    if (raw.trim() === "") return null;
    const parsed = parseWholeDollar(raw);
    return parsed === null ? undefined : parsed;
  };
  const threshold = parseOptional(fields.threshold);
  const maxThreshold = parseOptional(fields.maxThreshold);
  const firstMonthlyThreshold = parseOptional(fields.firstMonthlyThreshold);
  const maxFirstMonthlyThreshold = parseOptional(
    fields.maxFirstMonthlyThreshold
  );
  if (
    threshold === undefined ||
    maxThreshold === undefined ||
    firstMonthlyThreshold === undefined ||
    maxFirstMonthlyThreshold === undefined ||
    (threshold !== null && maxThreshold !== null && maxThreshold < threshold) ||
    (firstMonthlyThreshold !== null &&
      maxFirstMonthlyThreshold !== null &&
      maxFirstMonthlyThreshold < firstMonthlyThreshold)
  ) {
    return null;
  }
  return {
    threshold,
    maxThreshold,
    firstMonthlyThreshold,
    maxFirstMonthlyThreshold,
  };
}

const GIFT_HELP =
  "Checked on every donation. The user is notified when the gift amount falls within this range.";
const FIRST_MONTHLY_HELP =
  "Only applies to a donor's first monthly (recurring) gift. That gift notifies the user if it falls within either the gift range or this range, so you can hear about every new monthly donor even when the gift minimum is higher.";

function HelpIcon(props: { id: string; text: string }) {
  return (
    <>
      <span data-tip data-for={props.id} className={s.helpIcon}>
        <i className="fa fa-question-circle" />
      </span>
      <ReactTooltip
        id={props.id}
        type="info"
        place="top"
        effect="solid"
        multiline
        className={s.helpTooltip}
      >
        {props.text}
      </ReactTooltip>
    </>
  );
}

function ThresholdFields(props: {
  idPrefix: string;
  fields: ThresholdState;
  disabled: boolean;
  onChange: (fields: ThresholdState) => void;
}) {
  const field = (
    key: keyof ThresholdState,
    idSuffix: string,
    label: string,
    help: string
  ) => {
    const id = `${props.idPrefix}-${idSuffix}`;
    return (
      <FormGroup className={s.thresholdField}>
        <label className={s.label} htmlFor={id}>
          {label}
          <HelpIcon id={`${id}-help`} text={help} />
        </label>
        <BaseInput
          type="number"
          name={id}
          id={id}
          value={props.fields[key]}
          disabled={props.disabled}
          onChange={event =>
            props.onChange({
              ...props.fields,
              [key]: event.target.value,
            })
          }
        />
      </FormGroup>
    );
  };
  return (
    <div className={s.thresholdRow}>
      {field("threshold", "threshold", "Gift minimum ($)", GIFT_HELP)}
      {field("maxThreshold", "maxThreshold", "Gift maximum ($)", GIFT_HELP)}
      {field(
        "firstMonthlyThreshold",
        "firstMonthly",
        "First monthly minimum ($)",
        FIRST_MONTHLY_HELP
      )}
      {field(
        "maxFirstMonthlyThreshold",
        "maxFirstMonthly",
        "First monthly maximum ($)",
        FIRST_MONTHLY_HELP
      )}
      <p className={s.thresholdHelp}>
        Leave a minimum or maximum blank for no limit. Leave all four blank to
        match every gift.
      </p>
    </div>
  );
}

export default withStyles(s)(ThresholdFields);
